/* eslint-disable react-hooks/exhaustive-deps */
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import { EditIcon, TrashAltIcon } from "assets/icons";
import { t } from "i18next";
import { useRefetchQuery } from "libs/reactQuery";
import { ProductCell } from "modules/products";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState
} from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import { DateTableCell, Switch, TableItemActionsControl } from "ui";

import { useUpdateAdProduct } from "../apis";
import { AdProductSource } from "../constants";
import { IAdProduct, VariantPreview } from "../whatsappAdProducts.types";

import styles from "./useAdProductsColumns.module.scss";

const columnHelper = createColumnHelper<IAdProduct>();

interface ColumnProps {
  variantsById: Map<string, VariantPreview>;
  onOpenEdit: () => void;
  onOpenDelete: () => void;
  setAdProductId: Dispatch<SetStateAction<string | undefined>>;
}

const useAdProductsColumns = ({
  variantsById,
  onOpenEdit,
  onOpenDelete,
  setAdProductId
}: ColumnProps) => {
  const refetchList = useRefetchQuery(["whatsappAdProducts"]);

  const [openedItemOptionsIndex, setOpenedItemOptionsIndex] = useState<
    number | null
  >(null);

  const { mutate: mutateUpdate, isLoading: isUpdating } = useUpdateAdProduct({
    onSuccess: () => {
      refetchList();
      toast.success(t("Ad mapping was updated successfully"));
    }
  });

  const handleMenuButtonClick = useCallback(
    (
      cell: CellContext<IAdProduct, string | undefined>,
      handleOpenModal: () => void
    ) => {
      return () => {
        handleOpenModal();
        setAdProductId(cell.getValue());
      };
    },
    [setAdProductId]
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor((row) => row.productVariantIds, {
        id: "product",
        header: t("Products"),
        enableSorting: false,
        size: 30,
        minSize: 30,
        cell: (cell) => {
          const [variantId, ...others] = cell.getValue();
          const more =
            others.length > 0 ? (
              <span className={styles.moreBadge}>
                {t("+{{count}} more", { count: others.length })}
              </span>
            ) : null;
          const preview = variantsById.get(variantId);
          if (!preview) {
            return (
              <div className={styles.productsCell}>
                <span className={styles.mono}>{variantId}</span>
                {more}
              </div>
            );
          }
          const meta = [preview.categoryName, preview.colorValue]
            .filter(Boolean)
            .join(" · ");
          const cellContent = (
            <ProductCell
              image={preview.image as never}
              name={preview.name}
              subtitle={meta}
            />
          );
          if (preview.categoryId && preview.productId) {
            return (
              <div className={styles.productsCell}>
                <Link
                  className={styles.productLink}
                  to={`/products/details/${preview.categoryId}/${preview.productId}/${preview.id}/boxes`}
                  target="_blank"
                  onClick={(e) => e.stopPropagation()}
                >
                  {cellContent}
                </Link>
                {more}
              </div>
            );
          }
          return (
            <div className={styles.productsCell}>
              {cellContent}
              {more}
            </div>
          );
        }
      }),
      columnHelper.accessor((row) => row.sourceId, {
        id: "sourceId",
        header: t("Source ID"),
        size: 18,
        minSize: 18,
        cell: (cell) => <p className={styles.mono}>{cell.getValue()}</p>
      }),
      columnHelper.accessor((row) => row.label, {
        id: "label",
        header: t("Label"),
        size: 16,
        minSize: 16,
        cell: (cell) => (
          <p className={styles.label}>{cell.getValue() ?? "—"}</p>
        )
      }),
      columnHelper.accessor(
        (row) =>
          row.source === AdProductSource.AdName ? t("Ad name") : t("Manual"),
        {
          id: "source",
          header: t("Source"),
          size: 10,
          minSize: 10,
          cell: (cell) => <p>{cell.getValue()}</p>
        }
      ),
      columnHelper.accessor((row) => row.isActive, {
        id: "isActive",
        header: t("Active"),
        size: 8,
        minSize: 8,
        cell: (cell) => (
          <Switch
            checked={cell.getValue()}
            disabled={isUpdating}
            onChange={(e) =>
              mutateUpdate({
                id: cell.row.original.id,
                body: { isActive: e.target.checked }
              })
            }
          />
        )
      }),
      columnHelper.accessor((row) => row.updatedAt, {
        id: "updatedAt",
        header: t("Updated"),
        size: 12,
        minSize: 12,
        cell: (cell) => <DateTableCell date={cell.getValue()} />
      }),
      columnHelper.accessor((row) => row.id, {
        id: "action",
        header: "",
        enableSorting: false,
        size: 4,
        minSize: 4,
        cell: (cell) => (
          <TableItemActionsControl
            actionsList={[
              {
                label: "Edit",
                Icon: <EditIcon fill="#090914" width={18} height={18} />,
                onClick: handleMenuButtonClick(cell, onOpenEdit)
              },
              {
                label: "Delete",
                Icon: <TrashAltIcon fill="#ff0033" width={18} height={18} />,
                labelColor: "#ff0033",
                onClick: handleMenuButtonClick(cell, onOpenDelete)
              }
            ]}
            rowIndex={cell.row.index}
            openedItemOptionsIndex={openedItemOptionsIndex}
            setOpenedItemOptionsIndex={setOpenedItemOptionsIndex}
          />
        )
      })
    ],
    [
      handleMenuButtonClick,
      isUpdating,
      mutateUpdate,
      onOpenDelete,
      onOpenEdit,
      openedItemOptionsIndex,
      variantsById
    ]
  );

  return { columns };
};

export default useAdProductsColumns;
